import { useState } from 'react';
import { useEvaluationDetails } from '../../hooks/home/markView/useEvaluationDetails';
import { useMarkSubmission } from '../../hooks/home/markView/useMarkSubmission';

function MarkView({ studentId, onBack, onSubmitSuccess }) {
    const [refreshKey, setRefreshKey] = useState(0);
    const { evaluationData, isLoading } = useEvaluationDetails(studentId, onBack, refreshKey);
    const [entryStatus, setEntryStatus] = useState(null);

    const handleRefresh = () => {
        setEntryStatus(null);
        setRefreshKey(k => k + 1);
        onSubmitSuccess?.();
    };

    const normalizedStatus = entryStatus !== null ? entryStatus : (evaluationData?.entry_status ?? 'pending');

    const { cca, ese, isSubmitting, handleSubmit, handleCancel, handleCcaChange, handleEseChange } =
        useMarkSubmission(
            studentId,
            evaluationData,
            onBack,
            onSubmitSuccess,
            handleRefresh,
            (newStatus) => setEntryStatus(newStatus)
        );

    if (isLoading) {
        return (
            <div className="card">
                <div className="card-body">
                    <div className="text-center py-5">
                        <div className="spinner-border" role="status" style={{ color: '#581f37' }}>
                            <span className="visually-hidden">Loading...</span>
                        </div>
                        <p className="mt-3">Loading student details...</p>
                    </div>
                </div>
            </div>
        );
    }

    if (!evaluationData) {
        return (
            <div className="card">
                <div className="card-body text-center py-5">
                    <i className="fa fa-exclamation-triangle fa-3x text-warning mb-3" />
                    <p>No student data available</p>
                    <button className="btn btn-primary" onClick={onBack}>Back</button>
                </div>
            </div>
        );
    }

    const isEntryLocked = normalizedStatus === 'submitted';
    const isNa = normalizedStatus === 'na';
    const isFormDisabled = isEntryLocked || isNa;

    const isRevertedStatus = normalizedStatus === 'reverted';
    const hasRevertFlag = evaluationData?.revert === '1';

    const showOnlyRevertBadge = isRevertedStatus;
    const showExtraRevertBadge = hasRevertFlag && !isRevertedStatus;
    
    const statusConfig = {
        pending: {
            label: 'Pending',
            badgeClass: 'bg-warning text-dark',
            icon: 'fa-clock-o',
            alertClass: 'alert-info',
            alertText: 'This mark entry is pending submission.',
        },
        draft: {
            label: 'Draft',
            badgeClass: 'bg-info text-dark',
            icon: 'fa-file-text-o',
            alertClass: 'alert-info',
            alertText: 'This mark entry has been saved as a draft.',
        },
        submitted: {
            label: 'Submitted',
            badgeClass: 'bg-danger',
            icon: 'fa-lock',
            alertClass: 'alert-warning',
            alertText: 'This mark entry has been submitted and locked. It cannot be modified.',
        },
        na: {
            label: 'N/A',
            badgeClass: 'bg-secondary',
            icon: 'fa-ban',
            alertClass: 'alert-secondary',
            alertText: 'This entry is not available.',
        },
        reverted: {
            label: 'Reverted',
            badgeClass: '',
            badgeStyle: { backgroundColor: '#e65c00', color: '#fff' },
            icon: 'fa-solid fa-reply',
            alertClass: 'alert-warning',
            alertText: 'This entry has been reverted. Please review and resubmit.',
        },
    };

    const revertBadgeStyle = { backgroundColor: '#e65c00', color: '#fff' };
    const currentStatus = statusConfig[normalizedStatus] ?? null;

    return (
        <div className="card">
            <div className="col-md-12">
                <div className="card-header">
                    <div className="py-3 d-flex justify-content-between align-items-center flex-wrap gap-2">
                        <h5 style={{ color: '#581f37', fontWeight: 700, margin: 0, marginBottom: '12px' }}>
                            <i className="fa fa-edit me-2" />
                            Mark Entry
                        </h5>

                        <div className="d-flex align-items-center gap-2 flex-wrap">
                            {showOnlyRevertBadge ? (
                                <span className="badge" style={revertBadgeStyle}>
                                    <i className="fa-solid fa-reply me-1" />
                                    Reverted
                                </span>
                            ) : (
                                <>
                                    {currentStatus && (
                                        <span
                                            className={`badge ${currentStatus.badgeClass}`}
                                            style={currentStatus.badgeStyle || {}}
                                        >
                                            <i className={`fa ${currentStatus.icon} me-1`} />
                                            {currentStatus.label}
                                        </span>
                                    )}

                                    {showExtraRevertBadge && (
                                        <span className="badge" style={revertBadgeStyle}>
                                            <i className="fa-solid fa-reply me-1" />
                                            Reverted
                                        </span>
                                    )}
                                </>
                            )}
                        </div>
                    </div>
                </div>

                <div className="card-body">
                    {(isRevertedStatus || hasRevertFlag) && (
                        <div className="alert alert-warning mb-3 d-flex align-items-start gap-2" role="alert">
                            <i className="fa-solid fa-reply mt-1" style={{ color: '#e65c00', flexShrink: 0 }} />
                            <div>
                                <strong>Reverted:</strong>{' '}
                                {statusConfig.reverted.alertText}
                            </div>
                        </div>
                    )}

                    {isFormDisabled && !isRevertedStatus && !hasRevertFlag && currentStatus && (
                        <div className={`alert ${currentStatus.alertClass} mb-3`} role="alert">
                            <i className={`fa ${currentStatus.icon} me-2`} />
                            {currentStatus.alertText}
                        </div>
                    )}

                    <div className="row">
                        <div className="col-md-12">
                            <div className="card bg-light border-0">
                                <div className="card-body">
                                    <div className="row text-dark">
                                        <div className="col-md-6 mb-2"><strong>Student:</strong> {evaluationData.student}</div>
                                        <div className="col-md-6 mb-2"><strong>Exam:</strong> {evaluationData.examtitle}</div>
                                        <div className="col-md-6 mb-2"><strong>Course:</strong> {evaluationData.course}</div>
                                        <div className="col-md-6 mb-2"><strong>Discipline:</strong> {evaluationData.discipline}</div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <form onSubmit={(e) => { e.preventDefault(); if (!isFormDisabled) handleSubmit(); }}>
                        <div className="card bg-light border-0 text-dark">
                            <div className="card-body">
                                <div className="row mb-1">
                                    <div className="col-md-12">
                                        <label className="form-label fw-bold" htmlFor="ccaEntry">CCA Mark</label>
                                        <div className="input-group" id="inputGroup-cca">
                                            <input
                                                type="number"
                                                id="ccaEntry"
                                                name="ccaEntry"
                                                className="form-control custom-input main-input"
                                                placeholder="Enter CCA mark"
                                                value={cca !== '' && cca !== null && cca !== undefined ? Math.floor(Number(cca)) : ''}
                                                step="1" min="0"
                                                max={evaluationData.cca_max || 'N/A'}
                                                autoComplete="off"
                                                onChange={handleCcaChange}
                                                onWheel={(e) => e.target.blur()}
                                                onKeyDown={(e) => ['e', 'E', '.', ',', '+', '-'].includes(e.key) && e.preventDefault()}
                                                onInput={(e) => e.target.value = e.target.value.replace(/[^0-9]/g, '')}
                                                disabled={isSubmitting || isFormDisabled}
                                                readOnly={isFormDisabled}
                                                title={`${isFormDisabled ? "Entry is not editable" : "Enter CCA mark"}, Enter a value between 0 and ${evaluationData.cca_max || 100}`}
                                            />
                                            <div className="form-floating">
                                                <input type="text" className="form-control custom-input bg-light text-dark" value={evaluationData.cca_max || 'N/A'} disabled id="ccaMaxMarkInput" style={{ fontSize: '0.9rem' }} readOnly />
                                                <label htmlFor="ccaMaxMarkInput">Max Mark</label>
                                            </div>
                                        </div>
                                        <small className="text-muted">
                                            {(() => {
                                                const maxCO = 11;
                                                const needed = Number(evaluationData.outcome_number);
                                                const values = [];
                                                for (let i = 1; i <= maxCO; i++) {
                                                    const val = evaluationData[`interal_co${i}`];
                                                    if (val !== null && val !== undefined && val !== '') values.push({ num: i, val });
                                                }
                                                const finalValues = !needed || needed === 0 ? values : values.slice(0, needed);
                                                return finalValues.map(({ num, val }) => (
                                                    <span key={num} className="me-2"><strong>CO{num}:</strong> {val}</span>
                                                ));
                                            })()}
                                        </small>
                                    </div>
                                </div>

                                <div className="row mb-4">
                                    <div className="col-md-12">
                                        <label className="form-label fw-bold" htmlFor="eseEntry">ESE Mark</label>
                                        <div className="input-group" id="inputGroup-ese">
                                            <input
                                                type="number"
                                                id="eseEntry"
                                                name="eseEntry"
                                                className="form-control custom-input main-input"
                                                placeholder="Enter ESE mark"
                                                value={ese !== '' && ese !== null && ese !== undefined ? Math.floor(Number(ese)) : ''}
                                                step="1" min="0"
                                                max={evaluationData.ese_max || 'N/A'}
                                                autoComplete="off"
                                                onChange={handleEseChange}
                                                onWheel={(e) => e.target.blur()}
                                                onKeyDown={(e) => ['e', 'E', '.', ',', '+', '-'].includes(e.key) && e.preventDefault()}
                                                onInput={(e) => e.target.value = e.target.value.replace(/[^0-9]/g, '')}
                                                disabled={isSubmitting || isFormDisabled}
                                                readOnly={isFormDisabled}
                                                title={`${isFormDisabled ? "Entry is not editable" : "Enter ESE mark"}, Enter a value between 0 and ${evaluationData.ese_max || 100}`}
                                            />
                                            <div className="form-floating">
                                                <input type="text" className="form-control custom-input bg-light text-dark" value={evaluationData.ese_max || 'N/A'} disabled id="eseMaxMarkInput" style={{ fontSize: '0.9rem' }} readOnly />
                                                <label htmlFor="eseMaxMarkInput">Max Mark</label>
                                            </div>
                                        </div>
                                        <small className="text-muted">
                                            {(() => {
                                                const maxCO = 11;
                                                const needed = Number(evaluationData.outcome_number);
                                                const values = [];
                                                for (let i = 1; i <= maxCO; i++) {
                                                    const val = evaluationData[`external_co${i}`];
                                                    if (val) values.push({ num: i, val });
                                                }
                                                const finalValues = !needed || needed === 0 ? values : values.slice(0, needed);
                                                return finalValues.map(({ num, val }) => (
                                                    <span key={num} className="me-2"><strong>CO{num}:</strong> {val}</span>
                                                ));
                                            })()}
                                        </small>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="row mt-4">
                            <div className="col-md-12 d-flex justify-content-center gap-3">
                                <button type="button" className="btn btn-light px-4" onClick={handleCancel} disabled={isSubmitting}>
                                    Back
                                </button>
                                <button
                                    type="submit"
                                    className="btn btn-primary px-4"
                                    disabled={isSubmitting || (!cca && !ese) || isFormDisabled}
                                    title={isFormDisabled ? "Entry is not editable" : "Save marks"}
                                >
                                    {isSubmitting ? (
                                        <><span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />Saving...</>
                                    ) : (
                                        <>{isFormDisabled && <i className="fa fa-lock me-2" />}Save</>
                                    )}
                                </button>
                            </div>
                        </div>
                    </form>
                </div>
            </div>

            <style>{`
                .custom-input:focus { border-color:#581f37; box-shadow:0 0 0 0.25rem rgba(88,31,55,0.25); }
                .input-group .form-floating { flex:0 0 auto; width:auto; min-width:120px; }
                .input-group .main-input { flex:1; }
                input[type="number"]::-webkit-inner-spin-button, input[type="number"]::-webkit-outer-spin-button { opacity:1; }
                input[readonly] { background-color:#e9ecef !important; cursor:not-allowed; }
            `}</style>
        </div>
    );
}

export default MarkView;